import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { X, Mail, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Modal för att vidarebefordra ärende till kommun via Resend (PDF + bilagor).
 */
export default function CaseForwardModal({ caseRef, caseTitle, docsCount, onClose, onSent }) {
  const { case_id } = useParams();
  const [munis, setMunis] = useState([]);
  const [slug, setSlug] = useState("stockholm");
  const [recipient, setRecipient] = useState("");
  const [defaultEmail, setDefaultEmail] = useState("");
  const [message, setMessage] = useState("");
  const [includeDocs, setIncludeDocs] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/municipalities").then((r) => setMunis(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!slug) return;
    api.get(`/municipalities/${slug}`)
      .then((r) => setDefaultEmail(r.data.email || ""))
      .catch(() => setDefaultEmail(""));
  }, [slug]);

  const send = async () => {
    setSending(true);
    try {
      const payload = {
        municipality_slug: slug,
        include_documents: includeDocs,
        extra_message: message || null,
      };
      if (recipient.trim()) payload.recipient_email = recipient.trim();
      const { data } = await api.post(`/crm/cases/${case_id}/forward`, payload);
      toast.success(`Skickat till ${data.to} (${data.attached_count} bilaga(or))`);
      onSent();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunde inte skicka");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-zinc-900/50 backdrop-blur-sm p-4">
      <div className="w-full max-w-lg bg-white border border-zinc-200 rounded-sm p-6 animate-fade-in-up"
           data-testid="forward-modal">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-xl font-medium" style={{ fontFamily: "Outfit" }}>Vidarebefordra ärende</h3>
            <p className="text-xs text-zinc-500 mt-1">{caseRef} · {caseTitle}</p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-zinc-100 rounded-sm"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Kommun</label>
            <select value={slug} onChange={(e) => setSlug(e.target.value)} data-testid="forward-municipality"
              className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm bg-white outline-none focus:border-blue-700 text-sm">
              {munis.map((m) => (
                <option key={m.slug} value={m.slug}>{m.name} ({m.region})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
              Mottagaradress {defaultEmail && <span className="text-zinc-400 normal-case font-normal">(förvalt: {defaultEmail})</span>}
            </label>
            <input value={recipient} onChange={(e) => setRecipient(e.target.value)}
              placeholder={defaultEmail || "kommun@example.se"} data-testid="forward-recipient"
              className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm" />
            <p className="mt-1 text-[10px] text-zinc-500">Lämna tomt för att använda kommunens standardadress.</p>
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Meddelande till kommun (valfritt)</label>
            <textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)}
              placeholder="Hej, härmed vidarebefordrar vi detta ärende..." data-testid="forward-message"
              className="mt-1 w-full px-3 py-2.5 border border-zinc-300 rounded-sm focus:border-blue-700 outline-none text-sm resize-none" />
          </div>
          <label className="flex items-center gap-2 cursor-pointer text-sm text-zinc-700">
            <input type="checkbox" checked={includeDocs} onChange={(e) => setIncludeDocs(e.target.checked)}
              data-testid="forward-include-docs" className="accent-blue-700" />
            Bifoga alla uppladdade dokument ({docsCount} st)
          </label>
          <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-sm text-xs text-blue-900">
            En PDF-sammanställning av ärendet skapas automatiskt och bifogas alltid.
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium hover:bg-zinc-100 rounded-sm">Avbryt</button>
          <button onClick={send} disabled={sending} data-testid="forward-send"
            className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white text-sm font-medium rounded-sm inline-flex items-center gap-2 disabled:opacity-60">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Skicka
          </button>
        </div>
      </div>
    </div>
  );
}
